import { useState, useMemo, type DragEvent } from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { Camera, Check, ArrowLeft } from 'lucide-react'
import { Album, type MemoirSummary } from "../../../../types"
import { albumRole } from '../lib/album-role'
import { useShareAlbum, useAlbum } from '../lib/hooks'
import { mergePhotoItems } from '../lib/merge-photo-items'
import FolderToolbar from './folder-toolbar'
import Breadcrumbs from './breadcrumbs'
import NotificationModal from './ui/notification-modal'
import ViewLinkModal from './view-link-modal'
import ShareCodeModal from './share-code-modal'
import RenamePhotoModal from './rename-photo-modal'
import DeleteAlbumModal from './delete-album-modal'
import FeedbackHost from './feedback-host'
import PhotoGallery, { type ViewMode } from './photo-gallery'
import SelectionActionBar from './selection-action-bar'
import MemoirPaymentNotice, { formatMemoirDate } from './memoir-payment-notice'
import MemoirRenewal from './memoir-renewal'
import MemoirHandover from './memoir-handover'
import AlbumGrid from './album-grid'
import EmptyAlbumDropzone from './empty-album-dropzone'
import { useFeedback } from '../hooks/use-feedback'
import { usePhotoSelection } from '../hooks/use-photo-selection'
import { usePhotoGridNavigation } from '../hooks/use-photo-grid-navigation'
import { useAlbumCollection } from '../hooks/use-album-collection'
import { useAlbumMutations } from '../hooks/use-album-mutations'
import { useAlbumDragDrop } from '../hooks/use-album-drag-drop'
import { useAlbumUpload } from '../hooks/use-album-upload'
import { usePhotoDownload } from '../hooks/use-photo-download'
import { usePhotoActions } from '../hooks/use-photo-actions'
import {
  getAlbumsInFolder,
  getBreadcrumbPath
} from '../lib/folder-manager'
import { folderLogger } from '../lib/logger'

/**
 * What this workspace shows (SOL-204). Every album lives in exactly one
 * Memoir, so the workspace is always scoped: to one of the user's own Memoirs,
 * where albums can be created and uploaded to, or to "Shared with you" — the
 * albums others handed over, which belong to THEIR Memoirs, so nothing can be
 * created there.
 *
 * The scope filters what is DISPLAYED. The mutation, drag and upload hooks
 * still receive the full album list, because the localStorage merge they
 * write back (use-album-collection) is per user, not per Memoir — handing them
 * a filtered list would drop every other Memoir's albums from it.
 */
export type WorkspaceScope =
  // viewOnly: an invitee looking at a Memoir they have accepted but not yet
  // been handed (SOL-185): no uploads, no owner controls, no access log.
  | { kind: 'memoir'; memoir: MemoirSummary; onMemoirChanged?: () => void; viewOnly?: boolean }
  | { kind: 'shared' }

interface AlbumListProps {
  className?: string
  currentUserId?: string
  scope: WorkspaceScope
}

export function isInScope(album: Album, scope: WorkspaceScope): boolean {
  if (scope.kind === 'memoir') return album.memoirId === scope.memoir.uuid
  // Shared = the server says the caller is a member, not the owner (SOL-185).
  return albumRole(album) === 'member'
}

// SOL-82. What the selection handlers read from an event, and nothing more
// (see SelectionEvent in use-photo-selection.ts). The keyboard path has no
// real event to forward -- the grid's keydown is handled by the navigation
// hook, which reports an INDEX -- so this supplies the modifier state that
// tells `handleCheckboxClick` whether to toggle one photo or extend a range.
const selectionEvent = (shiftKey: boolean) => ({
  shiftKey, metaKey: false, ctrlKey: false, stopPropagation: () => {},
})

const VIEW_MODE_KEY = 'memoar_view_mode'

export default function AlbumList({ className = '', currentUserId, scope }: AlbumListProps): JSX.Element {
  const navigate = useNavigate()
  const location = useLocation()
  const {
    albums, loading, error, hasUnsavedChanges,
    setAlbums, setHasUnsavedChanges, fetchAlbums, updateAlbums,
  } = useAlbumCollection(currentUserId)
  // Seeded from navigation state, not a URL param: this component has no
  // URL-based routing of its own (see the comment on handleDownloadPhotos's
  // async branch), so `openAlbumId` is how export-status.tsx's "back to the
  // album" button opens the SPECIFIC album a download came from instead of
  // just landing on the root grid. Read once, at mount, via the lazy
  // initializer — later navigations to "/" from elsewhere (e.g. "Sign Out")
  // carry no state and must not reopen a stale album.
  const [currentFolderId, setCurrentFolderId] = useState<string | undefined>(
    () => (location.state as { openAlbumId?: string } | null)?.openAlbumId,
  )
  const [selectedAlbumForShare, setSelectedAlbumForShare] = useState<Album | null>(null)
  const [shareModalOpen, setShareModalOpen] = useState(false)
  const [viewLinkAlbum, setViewLinkAlbum] = useState<{ uuid: string; name: string } | null>(null)

  const feedback = useFeedback()
  const { showNotification, closeNotification } = feedback

  const scopeMemoirId = scope.kind === 'memoir' ? scope.memoir.uuid : undefined

  const {
    handleCreateFolder, handleRenameAlbum, handleChangeDateAlbum,
    handleDeleteAlbum, handleDeleteAlbumConfirm, deleteModal,
  } = useAlbumMutations({
    albums, updateAlbums, fetchAlbums, currentFolderId, currentUserId, memoirId: scopeMemoirId, feedback,
  })

  const {
    draggedAlbum, isDragging, handleAlbumDragStart, handleAlbumDragEnd,
    handleAlbumDrop, clearDragState,
  } = useAlbumDragDrop({ albums, updateAlbums, feedback })

  const { shareAlbum, loading: shareLoading, error: shareError } = useShareAlbum()

  // Fetch album files when inside an album
  const { files: albumFiles, loading: filesLoading, refresh: refreshAlbumFiles } = useAlbum(
    currentFolderId && !currentFolderId.startsWith('temp_') ? currentFolderId : null
  )

  const upload = useAlbumUpload({
    albums, setAlbums, currentFolderId, setCurrentFolderId, fetchAlbums, refreshAlbumFiles, feedback,
  })
  const {
    fileInputRef, isDropzoneActive, uploadProgress, handleFileInputChange,
    handleDropzoneDragOver, handleDropzoneDragLeave,
  } = upload

  // A Memoir that takes no uploads right now (the server answers 402
  // MEMOIR_READONLY): refunded or disputed (SOL-206), lapsed, or in a paid
  // read-only year (SOL-185). `writable` is the server's word on all three.
  // The toolbar drops Upload and New Album; the entry points inside an album
  // stay, since the drop zone is also where the photos are shown, but say why
  // (by cause) instead of starting an upload the server would refuse.
  const viewOnly = scope.kind === 'memoir' && !!scope.viewOnly
  const readOnly = viewOnly || (scope.kind === 'memoir' && !scope.memoir.writable)
  const explainReadOnly = () => showNotification(
    'info',
    'Uploads are paused',
    viewOnly || scope.kind !== 'memoir'
      ? 'This Memoir is not yours yet. You can look at its photos until it is handed over to you.'
      : readOnlyReason(scope.memoir),
  )
  const handleUploadToAlbum = readOnly ? explainReadOnly : upload.handleUploadToAlbum
  const handleAddPhotosToCurrentAlbum = readOnly ? explainReadOnly : upload.handleAddPhotosToCurrentAlbum
  const handleDropzoneClick = readOnly ? explainReadOnly : upload.handleDropzoneClick
  const handleDropzoneDrop = readOnly
    ? (e: DragEvent) => { e.preventDefault(); handleDropzoneDragLeave(e); explainReadOnly() }
    : upload.handleDropzoneDrop

  // View mode state (persisted in localStorage)
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = localStorage.getItem(VIEW_MODE_KEY)
    return (saved as ViewMode) || 'medium'
  })

  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode)
    localStorage.setItem(VIEW_MODE_KEY, mode)
  }

  const {
    selectedPhotos, handlePhotoSelect, handleCheckboxClick,
    handleSelectAll, handleClearSelection,
  } = usePhotoSelection({ albumFiles, resetKey: currentFolderId })

  const { handleDownloadPhotos, handleDownloadAlbum } = usePhotoDownload({
    currentFolderId, albumFiles, albums, selectedPhotos,
    clearSelection: handleClearSelection, navigate, feedback,
  })

  const {
    handleSetCover, handleDeletePhotos, handleRenamePhoto, handleRenameSubmit, renameModal,
  } = usePhotoActions({
    currentFolderId, albumFiles, selectedPhotos, clearSelection: handleClearSelection,
    refreshAlbumFiles, fetchAlbums, feedback,
  })

  const isDevelopment = import.meta.env.DEV

  const mergedPhotoItems = useMemo(
    () => mergePhotoItems(albumFiles, uploadProgress.files),
    [albumFiles, uploadProgress.files],
  )

  // SOL-82. Space/Enter and Shift+Arrow route back through the SAME selection
  // handlers the mouse uses -- `handleCheckboxClick` is the additive-toggle
  // entry point, and passing `shiftKey` to it is exactly what a Shift+click
  // does. A synthetic event object is the seam: these handlers read four
  // members, all of which a real event has too (see SelectionEvent).
  const photoNavigation = usePhotoGridNavigation({
    itemCount: mergedPhotoItems.length,
    onToggle: (index) => {
      const file = albumFiles[index]
      if (file) handleCheckboxClick(file.id, index, selectionEvent(false))
    },
    onExtend: (index) => {
      const file = albumFiles[index]
      if (file) handleCheckboxClick(file.id, index, selectionEvent(true))
    },
  })

  const handleUploadComplete = () => {
    folderLogger.info('Upload completed, refreshing albums list')
    fetchAlbums()
  }

  const handleShareAlbum = async (albumId: string) => {
    const album = albums.find(a => a.uuid === albumId)
    if (!album) return

    // Prevent sharing of temporary local albums
    if (album.uuid.startsWith('temp_')) {
      showNotification('info', 'Cannot Share', 'This album is empty and cannot be shared. Upload photos to it first.')
      return
    }

    setSelectedAlbumForShare(album)
    setShareModalOpen(true)
  }

  const handleShareSubmit = async (expiresInHours: number = 168) => {
    if (!selectedAlbumForShare) return

    try {
      const response = await shareAlbum(selectedAlbumForShare.uuid, { expiresInHours })

      setShareModalOpen(false)
      setSelectedAlbumForShare(null)

      // Show success message with OTP
      showNotification('success', 'Album Shared!', 'Share this code with others to give them access.', {
        copyableText: response.otp,
        copyLabel: 'Sharing Code',
        details: `Expires: ${new Date(response.expiresAt).toLocaleString()}`
      })
    } catch (error) {
      console.error('Failed to share album:', error)
      showNotification('error', 'Share Failed', shareError || 'Failed to share album. Please try again.')
    }
  }

  const handleNavigateToFolder = (folderId?: string) => {
    folderLogger.debug('Navigating to folder', {
      fromFolderId: currentFolderId,
      toFolderId: folderId
    })

    // Clear drag state immediately when navigating
    clearDragState()

    setCurrentFolderId(folderId)
  }

  const handleAlbumDoubleClick = (albumId: string) => {
    folderLogger.debug('Double-clicking album', {
      fromFolderId: currentFolderId,
      toFolderId: albumId
    })

    // Clear drag state immediately when entering an album
    clearDragState()

    setCurrentFolderId(albumId)
  }

  const handleSaveChanges = async () => {
    // TODO: Implement backend sync
    setHasUnsavedChanges(false)
    showNotification('info', 'Saved Locally', 'All changes are automatically saved locally. Backend sync will be available in a future update.')
  }

  folderLogger.debug('Album list render state', {
    currentFolderId,
    totalAlbums: albums.length
  })

  if (loading) {
    return (
      <div className={`album-list-loading ${className}`}>
        <p>Loading albums...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className={`album-list-error ${className}`}>
        <p>Error: {error}</p>
      </div>
    )
  }

  // Get albums for current folder, within this workspace's scope only
  const scopedAlbums = albums.filter((a) => isInScope(a, scope))
  const currentAlbums = getAlbumsInFolder(scopedAlbums, currentFolderId)
  const breadcrumbPath = getBreadcrumbPath(scopedAlbums, currentFolderId)
  // One "Access log" link per Memoir shown: the one Memoir for an owner, and
  // each distinct Memoir behind the claimed albums in "Shared with you".
  // An invitee's access-log request answers 404, so they get no link (SOL-185).
  const accessLogMemoirIds = scope.kind === 'memoir'
    ? (scope.viewOnly ? [] : [scope.memoir.uuid])
    : [...new Set(
        scopedAlbums
          .filter((a) => a.claimedBy === currentUserId) // claimants only
          .map((a) => a.memoirId)
          .filter((id): id is string => !!id),
      )]
  const scopeTitle = scope.kind === 'memoir' ? scope.memoir.name : 'Shared with you'
  // Above the toolbar in both render branches: the way back to the Memoirs
  // home, and — for a Memoir whose payment was reversed — why uploads to it
  // are refused, before anyone tries one.
  const scopeHeader = (
    <div className="memoir-scope-header">
      <button className="memoir-scope-header__back" onClick={() => navigate('/')}>
        <ArrowLeft size={16} />
        <span>Your Memoirs</span>
      </button>
      <h2 className="memoir-scope-header__title">{scopeTitle}</h2>
      <div className="memoir-scope-header__links">
        {accessLogMemoirIds.map((id) => {
          // In "Shared with you" there can be several; tell them apart by the albums behind each.
          const names = scope.kind === 'shared'
            ? scopedAlbums.filter((a) => a.memoirId === id).map((a) => a.name).join(', ')
            : ''
          return (
            <Link
              key={id}
              className="memoir-scope-header__link"
              to={`/memoir/${id}/access-log`}
              aria-label={names ? `Access log for ${names}` : undefined}
            >
              Access log{names && accessLogMemoirIds.length > 1 ? ` · ${names}` : ''}
            </Link>
          )
        })}
      </div>
      {scope.kind === 'memoir' && !scope.viewOnly && (
        <>
          <MemoirHandover memoir={scope.memoir} onChanged={scope.onMemoirChanged} />
          <MemoirPaymentNotice memoir={scope.memoir} onChanged={scope.onMemoirChanged} />
          <MemoirRenewal memoir={scope.memoir} onChanged={scope.onMemoirChanged} />
        </>
      )}
    </div>
  )

  folderLogger.debug('Current folder albums', {
    currentFolderId,
    albumCount: currentAlbums.length,
    albums: currentAlbums.map(a => ({ id: a.uuid, name: a.name, isEmpty: a.isEmpty }))
  })

  // Empty state: only when at root with no albums
  if (!currentFolderId && currentAlbums.length === 0) {
    return (
      <div className={`album-list ${className}`}>
        {scopeHeader}
        <FolderToolbar
          memoirId={readOnly ? undefined : scopeMemoirId}
          onCreateFolder={handleCreateFolder}
          onSaveChanges={handleSaveChanges}
          onUploadComplete={handleUploadComplete}
          hasUnsavedChanges={hasUnsavedChanges}
        />
        <div className="album-list-empty">
          <Camera size={64} strokeWidth={1.5} />
          {scope.kind === 'memoir' ? (
            <>
              <h3>This Memoir is waiting for its photos</h3>
              <p>{viewOnly
                ? 'Photos will appear here once the photographer adds them'
                : readOnly
                ? 'Uploads are paused while this Memoir is read-only'
                : 'Upload photos or create an album to start the delivery'}</p>
            </>
          ) : (
            <>
              <h3>Nothing has been shared with you yet</h3>
              <p>Albums a photographer hands over to you will appear here</p>
            </>
          )}
        </div>

        {/* No FeedbackHost here on purpose: this branch has no toast to
            protect (nothing to announce with zero albums) and no confirm
            modal to arm, so swapping in FeedbackHost would look identical
            (confirm-modal.tsx:26 returns null closed) -- see
            feedback-host.tsx:5-11 for the toast-ordering reason it excludes
            the toast in the first place. */}
        {/* Notification Modal */}
        <NotificationModal
          isOpen={feedback.notification.isOpen}
          title={feedback.notification.title}
          message={feedback.notification.message}
          variant={feedback.notification.variant}
          onClose={closeNotification}
          copyableText={feedback.notification.copyableText}
          copyLabel={feedback.notification.copyLabel}
          details={feedback.notification.details}
        />
      </div>
    )
  }

  return (
    <div className={`album-list ${className}`}>
      {scopeHeader}
      <FolderToolbar
        memoirId={readOnly ? undefined : scopeMemoirId}
        onCreateFolder={handleCreateFolder}
        onSaveChanges={handleSaveChanges}
        onUploadComplete={handleUploadComplete}
        hasUnsavedChanges={hasUnsavedChanges}
      />

      <Breadcrumbs
        key={`breadcrumbs-${currentFolderId || 'root'}`}
        breadcrumbPath={breadcrumbPath}
        rootLabel={scopeTitle}
        onNavigate={handleNavigateToFolder}
        onAlbumDrop={handleAlbumDrop}
        draggedAlbum={draggedAlbum}
        isDragging={isDragging}
      />

      {/* Photo grid when inside an album with files or uploading files */}
      {currentFolderId && mergedPhotoItems.length > 0 && (
        <PhotoGallery
          items={mergedPhotoItems}
          currentFolderId={currentFolderId}
          viewMode={viewMode}
          onViewModeChange={handleViewModeChange}
          selectedPhotos={selectedPhotos}
          onPhotoSelect={handlePhotoSelect}
          onCheckboxClick={handleCheckboxClick}
          isDropzoneActive={isDropzoneActive}
          onDropzoneDragOver={handleDropzoneDragOver}
          onDropzoneDragLeave={handleDropzoneDragLeave}
          onDropzoneDrop={handleDropzoneDrop}
          onAddMore={handleAddPhotosToCurrentAlbum}
          uploadProgress={uploadProgress}
          navigation={photoNavigation}
        />
      )}

      {/* Empty album drop zone - show only when no files AND no sub-albums AND not uploading */}
      {currentAlbums.length === 0 && currentFolderId && mergedPhotoItems.length === 0 && !filesLoading ? (
        <EmptyAlbumDropzone
          isDropzoneActive={isDropzoneActive}
          onDropzoneDragOver={handleDropzoneDragOver}
          onDropzoneDragLeave={handleDropzoneDragLeave}
          onDropzoneDrop={handleDropzoneDrop}
          onDropzoneClick={handleDropzoneClick}
          onAddPhotos={handleAddPhotosToCurrentAlbum}
          uploadProgress={uploadProgress}
        />
      ) : currentAlbums.length > 0 ? (
        <AlbumGrid
          currentAlbums={currentAlbums}
          isDevelopment={isDevelopment}
          onDragStart={handleAlbumDragStart}
          onDragEnd={handleAlbumDragEnd}
          onDoubleClick={handleAlbumDoubleClick}
          onRename={handleRenameAlbum}
          onDelete={handleDeleteAlbum}
          onChangeDate={handleChangeDateAlbum}
          onShare={handleShareAlbum}
          onDownload={handleDownloadAlbum}
          onAlbumDrop={handleAlbumDrop}
          onUpload={handleUploadToAlbum}
          draggedAlbum={draggedAlbum}
          isDragging={isDragging}
        />
      ) : null}

      {/* Share Modal */}
      {shareModalOpen && selectedAlbumForShare && (
        <ShareCodeModal
          albumName={selectedAlbumForShare.name}
          shareLoading={shareLoading}
          shareError={shareError}
          onGenerateCode={() => handleShareSubmit(168)}
          onShareForViewing={() => {
            setViewLinkAlbum({
              uuid: selectedAlbumForShare.uuid,
              name: selectedAlbumForShare.name,
            })
            setShareModalOpen(false)
          }}
          onClose={() => setShareModalOpen(false)}
        />
      )}

      {viewLinkAlbum && (
        <ViewLinkModal
          albumUuid={viewLinkAlbum.uuid}
          albumName={viewLinkAlbum.name}
          onClose={() => setViewLinkAlbum(null)}
        />
      )}

      {/* Rename Modal */}
      {renameModal.isOpen && (
        <RenamePhotoModal
          value={renameModal.value}
          originalLabel={renameModal.originalLabel}
          loading={renameModal.loading}
          onValueChange={renameModal.setValue}
          onSubmit={handleRenameSubmit}
          onClose={() => renameModal.close()}
        />
      )}

      {/* Delete Album Modal — typed-name confirmation for non-empty albums */}
      {deleteModal.isOpen && deleteModal.target && (
        <DeleteAlbumModal
          target={deleteModal.target}
          confirmText={deleteModal.confirmText}
          loading={deleteModal.loading}
          onConfirmTextChange={deleteModal.setConfirmText}
          onConfirm={handleDeleteAlbumConfirm}
          onClose={() => deleteModal.close()}
        />
      )}

      <FeedbackHost feedback={feedback} />

      {/* Selection Action Bar */}
      {selectedPhotos.size > 0 && (
        <SelectionActionBar
          selectedCount={selectedPhotos.size}
          currentFolderId={currentFolderId}
          onSelectAll={handleSelectAll}
          onRename={handleRenamePhoto}
          onSetCover={handleSetCover}
          onDownload={handleDownloadPhotos}
          onDelete={handleDeletePhotos}
          onDownloadAlbum={handleDownloadAlbum}
          onClose={handleClearSelection}
        />
      )}

      {/* Toast notification */}
      <div className={`toast ${feedback.toast.visible ? 'toast--visible' : ''}`}>
        <Check size={16} />
        <span>{feedback.toast.message}</span>
      </div>

      {/* Hidden file input for adding photos - accepts all image formats including RAW */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*,.cr2,.cr3,.nef,.arw,.dng,.orf,.rw2,.pef,.srw,.raf"
        onChange={handleFileInputChange}
        style={{ display: 'none' }}
      />
    </div>
  )
}

/** Why a Memoir takes no uploads, in the photographer's words, by cause. */
function readOnlyReason(memoir: MemoirSummary): string {
  if (memoir.status === 'grace' && !memoir.lapsed) {
    return 'Uploads to this Memoir are paused because its payment was refunded or disputed. '
      + 'Its photos can still be viewed, shared and downloaded.'
  }
  if (memoir.lapsed) {
    return 'This Memoir\'s paid year has ended, so it is read-only'
      + (memoir.deletionDueAt
        ? ` and will be deleted on ${formatMemoirDate(memoir.deletionDueAt)} unless you keep it for another year.`
        : '.')
      + ' Its photos can still be viewed, shared and downloaded.'
  }
  return 'This Memoir is read-only for this year: you can view and download; '
    + 'to add photos, pay for a year in which you can add them.'
}
